import { TellescopeModelFromName, useCalendarViewQuery } from "@tellescope/tanstack-query";
import { useCurrentTimestamp } from "@tellescope/ui";
import { isWithinInterval } from "date-fns/isWithinInterval";
import { addMinutes } from "date-fns/addMinutes";
import { startOfDay } from "date-fns/startOfDay";
import { useMemo } from "react";

export interface UseUpcomingAndOngoingEventsOptions {
  timezone?: string;
}

export interface UseUpcomingAndOngoingEventsResult {
  ongoingEvents: TellescopeModelFromName<"calendar_events">[];
  upcomingEvents: TellescopeModelFromName<"calendar_events">[];
}

export const useUpcomingAndOngoingEvents = ({
  timezone: _,
}: UseUpcomingAndOngoingEventsOptions = {}): UseUpcomingAndOngoingEventsResult => {
  const currentTimestamp = useCurrentTimestamp({ pollingRateInMs: 1000 * 60, snapTo: "minute" });
  const startOfToday = startOfDay(currentTimestamp);

  const eventsQuery = useCalendarViewQuery({
    referenceDate: startOfToday,
    lookbackInMS: 1000 * 60 * 60 * 24 * 7, // 1 week
    view: "week",
  });

  return useMemo<UseUpcomingAndOngoingEventsResult>(() => {
    const categorizedEvents = eventsQuery.data?.reduce<UseUpcomingAndOngoingEventsResult>(
      (acc, curr) => {
        const eventStartDate = new Date(curr.startTimeInMS);
        const eventEndDate = addMinutes(eventStartDate, curr.actualDuration ?? curr.durationInMinutes);

        const isOngoing = isWithinInterval(currentTimestamp, { start: eventStartDate, end: eventEndDate });
        if (isOngoing) {
          acc.ongoingEvents.push(curr);
          return acc;
        }

        if (currentTimestamp.getTime() >= curr.startTimeInMS) return acc;

        const currentUpcomingTime = acc.upcomingEvents[0]?.startTimeInMS;
        const isEarlierThanUpcoming = (currentUpcomingTime ?? Infinity) > curr.startTimeInMS;
        const isSameAsUpcoming = currentUpcomingTime ? curr.startTimeInMS === currentUpcomingTime : false;

        if (isEarlierThanUpcoming) {
          acc.upcomingEvents = [curr];
        } else if (isSameAsUpcoming) {
          acc.upcomingEvents.push(curr);
        }
        return acc;
      },
      { ongoingEvents: [], upcomingEvents: [] }
    ) ?? {
      ongoingEvents: [],
      upcomingEvents: [],
    };

    categorizedEvents.ongoingEvents.sort((a, b) => a.startTimeInMS - b.startTimeInMS);
    return categorizedEvents;
  }, [currentTimestamp, eventsQuery.data]);
};
